import { Box, Typography, Sheet, Button } from '@mui/joy';
import { Snowflake, CheckCircle } from '@phosphor-icons/react';

/**
 * Карточка тарифа на главной v2. Состояния:
 *  - subscription       → активный тариф: название, до какой даты, сколько осталось, прогресс, продлить, заморозка
 *  - pendingActivation  → оплачено, не активировано: кнопка «Активировать тариф» (или «активация истекла»)
 * Бесплатный/триал живут в FreeAccessCard.
 */
const GREEN = '#0F7B6C';
const CORAL = '#B84C31';
const ICE = '#2D7AB3';

const pluralDays = (n) => (n % 10 === 1 && n % 100 !== 11) ? 'день' : ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100)) ? 'дня' : 'дней';
// freeze_end приходит как YYYY-MM-DD; парсим как локальную дату, иначе в западных часовых поясах уезжает на день назад
const formatDate = (iso) => {
    if (!iso) return '';
    try {
        const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
        const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(iso);
        return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return iso; }
};
const PRODUCT = { qbank_only: 'QBank: тесты и клинические задачи', pro: 'APEX PRO: полный доступ', pro_promo: 'APEX PRO: полный доступ' };

function Card({ accent, children }) {
    return (
        <Sheet variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: '16px', borderColor: accent || 'divider', bgcolor: 'background.surface', display: 'flex', flexDirection: 'column', gap: 0.75, justifyContent: 'center' }}>
            {children}
        </Sheet>
    );
}
const Label = ({ color, children }) => (
    <Typography level="body-xs" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color }}>{children}</Typography>
);

export default function SubscriptionCard({ subscription, pendingActivation, freeze, onActivate, onRenew, onFreeze, activating = false }) {
    if (subscription) {
        const left = subscription.days_remaining ?? null;
        const total = subscription.days_total || null;
        const pct = left != null && total ? Math.max(0, Math.min(100, Math.round(((total - left) / total) * 100))) : null;
        const soon = left != null && left <= 14;
        return (
            <Card accent={freeze?.is_frozen ? ICE : undefined}>
                <Label color={GREEN}><CheckCircle size={12} weight="fill" style={{ verticalAlign: '-1px', marginRight: 4 }} />Твой тариф</Label>
                <Typography sx={{ fontSize: { xs: 20, sm: 22 }, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                    {subscription.name}{subscription.period ? ` · ${subscription.period}` : ''}
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Активен до <Box component="b" sx={{ color: 'text.primary' }}>{subscription.expires_at}</Box>
                    {left != null && <>{' · '}<Box component="span" sx={{ color: soon ? CORAL : 'text.secondary', fontWeight: soon ? 700 : 400 }}>осталось {left} {pluralDays(left)}</Box></>}
                </Typography>
                {pct != null && (
                    <Box data-testid="sub-progress" data-pct={pct} sx={{ mt: 0.5, height: 6, borderRadius: 3, bgcolor: 'divider', overflow: 'hidden' }}>
                        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: soon ? CORAL : GREEN }} />
                    </Box>
                )}
                {freeze?.is_frozen && (
                    <Typography level="body-sm" sx={{ color: ICE, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Snowflake size={14} weight="bold" /> Заморожена до {formatDate(freeze.freeze_end)}
                    </Typography>
                )}
                {(soon || freeze?.can_freeze) && !freeze?.is_frozen && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.75 }}>
                        {soon && <Button size="sm" onClick={onRenew} sx={{ bgcolor: GREEN, color: '#fff', fontWeight: 700, borderRadius: '10px', '&:hover': { bgcolor: '#0C655A' } }}>Продлить</Button>}
                        {freeze?.can_freeze && <Button size="sm" variant="soft" color="neutral" startDecorator={<Snowflake size={14} weight="bold" />} onClick={onFreeze} sx={{ borderRadius: '10px', fontWeight: 600 }}>Заморозить подписку</Button>}
                    </Box>
                )}
            </Card>
        );
    }
    if (pendingActivation && !pendingActivation.expired) {
        return (
            <Card accent={GREEN}>
                <Label color={GREEN}>✓ Тариф оплачен</Label>
                <Typography sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                    {PRODUCT[pendingActivation.product_code] || 'Тариф'}{pendingActivation.period ? ` · ${pendingActivation.period}` : ''}
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Срок начнётся с момента активации. Активировать можно в течение года{pendingActivation.activate_until ? `, до ${pendingActivation.activate_until}` : ''}.
                </Typography>
                <Box><Button loading={activating} onClick={() => onActivate?.(pendingActivation.uuid_pay)} sx={{ mt: 0.5, bgcolor: GREEN, color: '#fff', fontWeight: 700, borderRadius: '10px', px: 2.5, '&:hover': { bgcolor: '#0C655A' } }}>Активировать тариф</Button></Box>
            </Card>
        );
    }
    if (pendingActivation?.expired) {
        return (
            <Card accent={CORAL}>
                <Label color={CORAL}>Активация истекла</Label>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Год на активацию прошёл. <a href="https://t.me/ashurovmed" target="_blank" rel="noopener noreferrer" style={{ color: CORAL, fontWeight: 600 }}>Напишите в поддержку</a>, чтобы восстановить доступ.
                </Typography>
            </Card>
        );
    }
    return null;
}
